import { type ChangeEvent, type ReactNode, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import type { FieldError } from 'react-hook-form';

import { cn } from '@/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    className?: string;
    containerClassName?: string;
    label?: string;
    invalid?: boolean;
    error?: FieldError | boolean;
    inverse?: boolean;
    children?: ReactNode;
}

export const inputClasses = `
text-body-md-400 outline-stroke-lightest bg-surface-white flex h-14 w-full rounded-[1px] outline-2 px-5 py-0 text-black
hover:outline-stroke-subtle
placeholder:text-subtle
disabled:bg-surface-disabled disabled:outline-stroke-disabled disabled:cursor-not-allowed disabled:opacity-50
focus-visible:ring-stroke-focus focus-visible:ring-3 focus-visible:outline-none focus-visible:outline-surface-white
`;

const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ containerClassName, className, label, type, invalid, inverse, children, error, ...props }, ref) => {
        return (
            <div className={cn('relative isolate', containerClassName)}>
                <input
                    id='input'
                    type={type}
                    className={cn(
                        inputClasses,
                        'file:border-0 file:bg-transparent file:text-sm file:font-medium',
                        '[&::-webkit-search-cancel-button]:hidden',
                        {
                            'pt-3': label,
                            'outline-inverse-subtle': inverse,
                            'outline-stroke-invalid': error || invalid,
                        },
                        className,
                    )}
                    ref={ref}
                    {...props}
                />
                {label && (
                    <label
                        htmlFor='input'
                        className='text-body-xs-400 text-subtle absolute top-2 left-5 transition-all'>
                        {label}
                    </label>
                )}

                {children}

                {typeof error === 'object' && error.message && (
                    <div role='alert' className='text-invalid text-body-xs-400'>
                        {error.message}
                    </div>
                )}
            </div>
        );
    },
);
Input.displayName = 'Input';

export { Input };

const FormInput = forwardRef<
    HTMLInputElement,
    Omit<InputProps, 'onChange' | 'onBlur'> & {
        onChange?: (value: string) => void;
        onBlur?: (value: string) => void;
        onAutocomplete?: (value: string) => void;
    }
>(({ value, onChange, onBlur, onAutocomplete, ...props }, ref) => {
    const [val, setValue] = useState(value || '');
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    useEffect(() => {
        if (value !== val) {
            setValue(value ? String(value) : '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;

        setValue(value);

        if (onChange) {
            onChange(value);
        }
        if (onAutocomplete && inputRef.current && document.activeElement !== (inputRef.current as HTMLInputElement)) {
            onAutocomplete(value);
        }
    };

    const onBlurHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;

        setValue(value.trim());

        if (onBlur) {
            onBlur(value.trim());
        }
    };

    return <Input {...props} ref={inputRef} value={val} onChange={onChangeHandler} onBlur={onBlurHandler} />;
});
FormInput.displayName = 'FormInput';

export { FormInput };
